import { useEffect, useRef, useState } from "react";
import { ModalShell } from "../ModalShell";
import { useAuth } from "../../hooks/useAuth";
import { type InstanceMember, useInstanceMembers } from "../../hooks/useInstanceMembers";
import { isValidEmail } from "../../utils/email";
import dialogStyles from "../../pages/admin/AdminFeatureFlags.module.css";
import styles from "./ShareInstanceModal.module.css";

const statusPillClass = (status: InstanceMember["status"]): string =>
  `${styles.pill} ${status === "active" ? styles.statusActive : styles.statusInvited}`;

const PlusIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

type ShareInstanceModalProps = {
  tenantId: string;
  instanceName: string;
  callerCanManage: boolean;
  onClose: () => void;
};

// Per-instance sharing. Simple model: the owner invites collaborators who get
// the same access, and can remove them. No roles to switch — managing is
// owner-only (the backend still enforces every rule).
export const ShareInstanceModal = ({
  tenantId,
  instanceName,
  callerCanManage,
  onClose,
}: ShareInstanceModalProps): JSX.Element => {
  const { user } = useAuth();
  const { status, team, error, inviteMember, removeMember } = useInstanceMembers(tenantId);

  const [flash, setFlash] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState<boolean>(false);
  const [removing, setRemoving] = useState<InstanceMember | null>(null);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);

  const canManage = callerCanManage && team !== null && team.callerRole === "OWNER";
  const selfEmail = user?.email ?? null;
  const titleId = "share-instance-dialog-title";

  const handleRemove = async (member: InstanceMember): Promise<void> => {
    setBusyEmail(member.email);
    setFlash(null);
    const result = await removeMember(member.email);
    setBusyEmail(null);
    setRemoving(null);
    if (result.ok) {
      setFlash(
        member.status === "invited"
          ? `Revoked the invite for ${member.email}.`
          : `Removed ${member.email} from ${instanceName}.`,
      );
    } else {
      setFlash(result.message);
    }
  };

  const memberCount = team === null ? 0 : team.members.length - 1;
  const atCapacity = team !== null && memberCount >= team.memberCap;

  return (
    <ModalShell onClose={onClose} titleId={titleId} size="sm">
      <header className={dialogStyles.dialogHeader}>
        <h3 className={dialogStyles.dialogTitle} id={titleId}>
          Share {instanceName}
        </h3>
        <button type="button" className={dialogStyles.closeBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <div className={styles.body}>
        <p className={styles.subtitle}>
          {canManage
            ? "Invite people to share access to this OpenClaw."
            : "People with access to this OpenClaw."}
        </p>

        {flash && <div className={styles.flash}>{flash}</div>}

        {status === "loading" && <div className={styles.stateCard}>Loading collaborators…</div>}
        {status === "error" && <div className={styles.stateCard}>{error ?? "Failed to load."}</div>}
        {status === "ready" && team !== null && (
          <>
            <ul className={styles.list} data-testid="instance-member-list">
              {team.members.map((member) => {
                const isOwnerRow = member.role === "OWNER";
                const isSelf = member.email === selfEmail;
                return (
                  <li key={member.email} className={styles.row} data-testid="instance-member-row">
                    <span className={styles.identity}>
                      <span className={styles.email}>{member.email}</span>
                      {isSelf ? <span className={styles.youTag}>(you)</span> : null}
                    </span>
                    <span className={styles.rowRight}>
                      <span className={statusPillClass(member.status)}>
                        <span className={styles.dot} aria-hidden="true" />
                        {member.status === "invited" ? "Invited" : "Active"}
                      </span>
                      {canManage && !isOwnerRow ? (
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={() => setRemoving(member)}
                          disabled={busyEmail === member.email}
                        >
                          {member.status === "invited" ? "Revoke invite" : "Remove"}
                        </button>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>

            {canManage && (
              <div className={styles.footer}>
                <button
                  type="button"
                  className={styles.primaryBtn}
                  onClick={() => setInviteOpen(true)}
                  disabled={atCapacity}
                  title={
                    atCapacity
                      ? "This instance has reached its collaborator limit — contact support to raise it."
                      : undefined
                  }
                >
                  <PlusIcon />
                  Invite member
                </button>
                <span className={styles.seats}>
                  {memberCount === 0 ? (
                    "No collaborators yet"
                  ) : (
                    <>
                      <span className={styles.seatsCount}>{memberCount}</span> of {team.memberCap} shared
                    </>
                  )}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {inviteOpen && (
        <InviteMemberDialog
          onClose={() => setInviteOpen(false)}
          onSend={async (email) => {
            const result = await inviteMember(email);
            if (result.ok) {
              setFlash(
                result.value.status === "invited"
                  ? `Invited ${result.value.email} — they'll get an email to set up their login.`
                  : `${result.value.email} now has access to ${instanceName}.`,
              );
              setInviteOpen(false);
              return { ok: true, message: "" };
            }
            return { ok: false, message: result.message };
          }}
        />
      )}

      {removing && (
        <RemoveMemberDialog
          member={removing}
          instanceName={instanceName}
          busy={busyEmail === removing.email}
          onClose={() => setRemoving(null)}
          onConfirm={() => void handleRemove(removing)}
        />
      )}
    </ModalShell>
  );
};

type InviteMemberDialogProps = {
  onClose: () => void;
  onSend: (email: string) => Promise<{ ok: boolean; message: string }>;
};

const InviteMemberDialog = ({ onClose, onSend }: InviteMemberDialogProps): JSX.Element => {
  const [email, setEmail] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const validEmail = isValidEmail(email);
  const titleId = "share-invite-dialog-title";

  return (
    <ModalShell onClose={onClose} titleId={titleId} size="sm">
      <header className={dialogStyles.dialogHeader}>
        <h3 className={dialogStyles.dialogTitle} id={titleId}>Invite a collaborator</h3>
        <button type="button" className={dialogStyles.closeBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!validEmail) {
            setError("Enter a valid email address.");
            return;
          }
          setSubmitting(true);
          setError(null);
          const result = await onSend(email);
          setSubmitting(false);
          if (!result.ok) setError(result.message);
        }}
      >
        <label className={dialogStyles.field}>
          <span>Email address</span>
          <input
            ref={inputRef}
            type="email"
            value={email}
            placeholder="teammate@company.com"
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) setError(null);
            }}
            required
            disabled={submitting}
          />
        </label>

        <p className={dialogStyles.hint}>
          New collaborators get an email to set up their login. Existing ZeroToClaw users get
          access right away.
        </p>

        {error && <p className={dialogStyles.errorBanner}>{error}</p>}

        <footer className={dialogStyles.footer}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primaryBtn}
            disabled={submitting || !validEmail}
            title={!validEmail ? "Enter a valid email" : undefined}
          >
            {submitting ? "Sending…" : "Send invite"}
          </button>
        </footer>
      </form>
    </ModalShell>
  );
};

type RemoveMemberDialogProps = {
  member: InstanceMember;
  instanceName: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const RemoveMemberDialog = ({
  member,
  instanceName,
  busy,
  onClose,
  onConfirm,
}: RemoveMemberDialogProps): JSX.Element => {
  const titleId = "share-remove-dialog-title";
  const pending = member.status === "invited";

  return (
    <ModalShell onClose={onClose} titleId={titleId} size="sm">
      <header className={dialogStyles.dialogHeader}>
        <h3 className={dialogStyles.dialogTitle} id={titleId}>
          {pending ? "Revoke invite" : "Remove collaborator"}
        </h3>
        <button type="button" className={dialogStyles.closeBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <p className={dialogStyles.hint}>
        {pending
          ? `This cancels the invite for ${member.email}. They won't be able to set up an account.`
          : `${member.email} will immediately lose access to ${instanceName} and won't be able to sign in. You can re-invite them later.`}
      </p>

      <footer className={dialogStyles.footer}>
        <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button type="button" className={styles.dangerBtn} onClick={onConfirm} disabled={busy}>
          {busy ? "Removing…" : pending ? "Revoke invite" : "Remove member"}
        </button>
      </footer>
    </ModalShell>
  );
};
